import { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectAdminAuth, setAdminAuth, clearAdminAuth } from "../../../redux/features/appData";
import { apiVerifyAdminSecret } from "../../../services/AdminService";

interface AdminAuthHookState {
  adminSecret: string;
  verifying: boolean;
  error: string | null;
}

export const useAdminAuth = () => {
  const dispatch = useDispatch();
  const adminAuth = useSelector(selectAdminAuth);
  const [localState, setLocalState] = useState<AdminAuthHookState>({
    adminSecret: "",
    verifying: false,
    error: null,
  });

  const setAdminSecret = useCallback((secret: string) => {
    setLocalState(prev => ({ ...prev, adminSecret: secret, error: null }));
  }, []);

  const verifySecret = useCallback(async () => {
    setLocalState(prev => ({ ...prev, verifying: true, error: null }));
    
    try {
      const verified = await apiVerifyAdminSecret(localState.adminSecret);
      if (verified) {
        dispatch(setAdminAuth({
          isAuthenticated: true,
          adminSecret: localState.adminSecret,
          verifiedAt: new Date(),
        }));
        setLocalState(prev => ({ 
          ...prev, 
          adminSecret: "", 
          verifying: false 
        }));
      } else {
        setLocalState(prev => ({ 
          ...prev, 
          error: "Invalid admin secret", 
          verifying: false 
        }));
      }
    } catch (err) {
      setLocalState(prev => ({ 
        ...prev, 
        error: "Failed to verify admin secret", 
        verifying: false 
      }));
    }
  }, [localState.adminSecret, dispatch]);

  const reset = useCallback(() => {
    dispatch(clearAdminAuth());
    setLocalState({
      adminSecret: "",
      verifying: false,
      error: null,
    });
  }, [dispatch]);

  return {
    isAuthenticated: adminAuth.isAuthenticated,
    adminSecret: localState.adminSecret,
    verifying: localState.verifying,
    error: localState.error,
    setAdminSecret,
    verifySecret,
    reset,
  };
};